<template>
  <div class="sidebar" :class="{'is-obfuscated': $route.name === 'channel-sign-up'}">
    <div v-if="!logo" class="sidebar-mobile__logo" @click="toggleSidebar">
      <span class="sidebar-mobile__logo-initial"></span>
    </div>
    <div v-if="logo" class="sidebar-mobile__logo" @click="toggleSidebar" :style="{ backgroundImage: `url('${logo}')`}"></div>
    <div class="sidebar-container" :class="{'is-visible': showSidebar}">
      <section class="sidebar-main">
        <header
          class="sidebar-main__header"
          @click="handleHeaderClick"
          @mouseenter="headerHover = true"
          @mouseleave="headerHover = false"
          :class="{
            'is-active': headerHover
          }">
          <div class="channel-logo" @mouseenter="logoHover = true" @mouseleave="logoHover = false">
            <image-upload :class="{'is-visible': logoHover}" class="channel-logo__action" v-if="owner && !logo" @upload="imageUploaded" @started="uploadStart" :text="'Add Logo'"/>
            <image-upload :class="{'is-visible': logoHover}" class="channel-logo__action" v-if="owner && logo" @upload="imageUploaded" @started="uploadStart" :text="'Update'"/>
            <div
              v-if="logo"
              class="channel-logo__asset"
              :style="{ backgroundImage: `url('${logo}')`}">
            </div>
            <div
              v-if="!logo"
              class="channel-logo__asset is-empty"
              :style="{ backgroundColor: logoColor }">
            </div>
          </div>
          <h1 class="channel-name">{{channel.name}}</h1>
          <p class="channel-description">{{channel.description}}</p>
        </header>
        <section class="sidebar-pages">
          <ff-sidelist
            class="is-published"
            data-test="published-list"
            v-if="livePages.length"
            :heading="'Recent'"
            :items="livePages"
            :route="$route.params"
            :action="goToPage"
            :listChange="updateLiveList"
          />
          <ff-sidelist
            v-if="draftPages && draftPages.length && user && owner && !preview"
            :heading="'Drafts'"
            :items="draftPages"
            :route="$route.params"
            :action="goToPage"
            :listChange="updateDraftList"
          />
          <ff-sidelist v-if="!draftPages.length && user && owner" :heading="'No Drafts'" :items="[]" :route="$route.params" :action="goToPage"/>
          <div v-if="!draftPages.length && user && owner" class="sidebar-pages__default">
            Everything starts with Pages, Create a draft. <ff-infotip :heading="'Pages'" :copy="'Pages hold everything: from blogs, to videos and PDFs. Create a page to start building content.'"/>
          </div>
          <ff-button
            data-test="create-page"
            class="is-primary"
            v-if="owner && !preview"
            :action="createPage"
            :text="'Create a Page'"/>
          <div v-if="!channel.pages" class="sidebar-pages__none">
            We're getting ready to launch {{channel.name}}! Sign Up for updates and exclusive content.
          </div>
        </section>
      </section>
      <footer>
        <connect-stripe
          v-if="owner && !preview && !stripeConnected"
          :text="'Setup Payments'"
          :sidebar="true"/>
        <ff-button
          v-if="owner && !preview && stripeConnected"
          class="sidebar-pages__item"
          :action="goToFans"
          :icon="'fans'"
          :text="'Your Fans'"/>
        <ff-button
          v-if="owner && !preview"
          class="sidebar-pages__item"
          :action="previewChannel"
          :icon="'preview'"
          :text="'Preview Channel'"/>
        <ff-button
          v-if="owner && !preview"
          class="sidebar-pages__item"
          :icon="'settings'"
          :action="goToSettings"
          :text="'Settings'"/>
        <ff-button
          v-if="user && (!owner || preview)"
          data-test="profile-button"
          class="sidebar-pages__item is-demure"
          :action="profile"
          :text="'Profile'"/>
        <ff-button
          data-test="signout-button"
          v-if="user"
          class="sidebar-pages__item is-demure"
          :action="signOut"
          :text="'Sign Out'"
          :class="{'is-owner': owner && !preview}"/>
        <ff-button
          v-else
          class="is-primary"
          data-test="signin-sidebar"
          :style="{ backgroundColor: uiColor }"
          :action="login"
          :text="'Sign Up / In'"/>
      </footer>
    </div>
  </div>
</template>

<script>
import persist from '../../mixins/persist'
import analytics from '../../mixins/analytics'
import { auth, db } from '../../helpers/firebase'
import { mapState } from 'vuex'
import FFSidelist from '../DesignSystem/FFSidelist'
import FFButton from '../DesignSystem/FFButton'
import ConnectStripe from './ConnectStripe'
import FFInfotip from '../DesignSystem/FFInfotip'
import ImageUpload from '../_Shared/ImageUpload'
import { deserializeImage, serializeImage } from '../../helpers/storage'
import _ from 'lodash'

export default {
  name: 'Sidebar',
  mixins: [ persist, analytics ],
  components: {
    'ff-sidelist': FFSidelist,
    'ff-button': FFButton,
    'connect-stripe': ConnectStripe,
    'ff-infotip': FFInfotip,
    'image-upload': ImageUpload
  },
  props: {
    msg: String,
    preview: String
  },
  data () {
    return {
      showSidebar: false,
      logo: null,
      logoHover: false,
      logoUploading: false,
      headerHover: false
    }
  },
  async mounted () {
    if (this.channel.logo) {
      this.logo = await deserializeImage(this.channel.logo)
    }
  },
  computed: {
    ...mapState(['user', 'channel', 'channel_id']),
    owner () {
      if (!this.user || !this.channel) return
      return this.user.uid === this.channel.owner
    },
    livePages: {
      get () {
        if (!this.channel.pages) return []
        let livePages = []
        this.channel.pages.forEach((item) => {
          if (item.tier && item.tier != '' && !item.parent) livePages.push(item)
        })
        return livePages
      },
      set (newValue) {
        return newValue
      }
    },
    draftPages: {
      get () {
        if (!this.channel.pages) return []
        let draftPages = []
        this.channel.pages.forEach((item) => {
          if (item.tier == '' || !item.tier && !item.parent) draftPages.push(item)
        })
        return draftPages
      },
      set (newValue) {
        return newValue
      }
    },
    stripeConnected () {
      return !!this.$store.state.channel.stripeAccountId
    },
    uiColor () {
      if (this.channel && this.channel.landing && this.channel.landing.color) {
        return `#${this.channel.landing.color}`
      }
      return `#072FFF`
    },
    logoColor () {
      if (this.channel && this.channel.landing && this.channel.landing.color) {
        return `#${this.channel.landing.color}`
      }
      return `#F0F0F0`
    }
  },
  methods: {
    goToSettings () {
      this.$router.push({name: 'channel-settings'})
    },
    goToFans () {
      this.$router.push({name: 'channel-fans'})
    },
    goToLanding () {
      this.showSidebar = false
      if (this.$route.name === 'channel-landing') return
      return this.$router.push({name: 'channel-landing'})
    },
    goToPage (id) {
      this.showSidebar = false
      if (this.$route.params.page_id === id) return
      return this.$router.push({name: 'page', params: { page_id: id }})
    },
    signOut () {
      auth.signOut().then(() => {
        return this.goToLanding()
      })
    },
    login () {
      this.$modal.show('login')
      this.track('[Channel] Clicked Sign Up', { location: 'sidebar' })
    },
    profile () {
      this.$router.push({name: 'profile'})
    },
    createPage () {
      return this.PagesCreate(this.channel_id, (id) => {
        this.track('[Fanfolk] Created Page')
        return this.goToPage(id)
      })
    },
    previewChannel () {
      window.open(`${window.location.href}?preview=true`, '_blank')
    },
    toggleSidebar () {
      this.showSidebar = !this.showSidebar
    },
    handleHeaderClick () {
      return this.goToLanding()
    },
    imageUploaded (result) {
      if (!result) return
      if (result.success) {
        let image = serializeImage(result.file.url)
        this.ChannelUpdate(this.channel_id, {
          logo: image
        }, () => {
          this.logo = result.file.url
        })
      }
    },
    uploadStart () {
      this.logoUploading = true
    },
    updateLiveList (data, list) {
      this.handleDragReorder(data, list, 'livePages')
    },
    updateDraftList (data, list) {
      this.handleDragReorder(data, list, 'draftPages')
    },
    handleDragReorder (data, list, computedList) {
      if (!data || !data.moved) return
      let batch = db.batch()

      let newList = _.forEach(list, (item, index) => {
        let newIndex = parseInt(index)
        item.index = newIndex
        let ref = this.getPageRef(item.id)
        batch.update(ref, { index: newIndex })
      })
      batch.commit().then(() => {
        this[computedList] = newList
      })
    },
    getPageRef (id) {
      let ref = db.collection('channels')
          .doc(this.channel_id)
          .collection('pages')
          .doc(id)
      return ref
    }
  }
}
</script>

<style scoped lang="scss">
  @import "../../assets/styles/app.scss";

  .sidebar-container {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: $sidebar-width;
    background: $color-white;
    border-right: 1px solid $color-border;
    color: $color-dark;
    text-align: left;
    box-sizing: border-box;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    overflow-y: scroll;
    overflow-x: hidden;
    padding-bottom: $unit-3;
  }

  .sidebar-main__header {
    padding: $unit-3 $unit-3 $unit-1;

    &.is-active {
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .channel-logo {
    width: 100%;
    height: calc(5rem + 2px);
    border-radius: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    box-sizing: border-box;
    margin-bottom: $unit-1;

    &__action {
      margin-left: 6rem;
      opacity: 0;

      .bx--label-description,
      .bx--file--label {
        display: none !important;
      }


      &.is-visible {
        opacity: 1;
      }
    }

    &__asset {
      border: 1px solid $color-demure;
      position: absolute;
      top: 0px;
      left: 0px;
      background-repeat: no-repeat;
      background-size: cover;
      width: 5rem;
      height: 5rem;
      border-radius: 3rem;
      z-index: 3;

      &.is-empty {
        background-color: rgba($color-demure, 0.3);
        border-color: transparent;
      }
    }

    .update {

    }
  }

  .channel-name {
    @include text('strong');
    margin: $unit-1/2 0;
  }

  .channel-description {
    @include text('ui');
  }

  .sidebar-pages__item {
    @include sidebar-item;
  }

  .sidebar-pages__default {
    padding: 0 0 0 $unit-3;
    font-size: $fontSize-normal;
    line-height: $lineHeight;
  }

  .is-primary {
    margin: $unit-2 0 $unit-2 $unit-3;
  }

  .sidebar-pages__create {
    margin-left: $unit-3;
  }

  .sidebar-mobile__logo {
    background: $color-almost-white;
    background-size: cover;
    background-repeat: no-repeat;
    color: $color-white;
    z-index: 9999;
    position: absolute;
    top: $unit-1;
    left: $unit-1;
    border-radius: $borderRadius*10;
    width: $fontSize-medium*1.5;
    height: $fontSize-medium*1.5;
    text-align: center;
    display: flex;
    display: none;

    &-initial {
      background: url('../../assets/img/hamburger.svg') no-repeat center;
      width: $unit-2;
      height: $unit-2;
      background-size: $unit-2;
      display: block;
      margin: auto;
    }
  }

  .sidebar-pages__none {
    @include text;
    background: $color-almost-white;
    border-radius: $borderRadius;
    padding: $unit-2;
    margin: $unit-1;
  }

  .sidebar.is-obfuscated {
    opacity: 0.05;
  }

  a[data-paid="true"] {
    color: #FF0000;
  }

  @import "../../assets/styles/_mobile.scss";
</style>
